import {
  Assessment,
  AssessmentResults,
  AssessmentState,
  ChecklistItemResultEnum,
  Details,
  FindingSuggestion,
  InstanceParentParams,
  LoadAssessment,
  LoadAssessmentPDF,
  NewAction,
  NewControl,
  Action,
  Control,
  SectionResult,
  AseResultInstanceCheck,
  LoadAssessmentElementPDF,
  LoadAssessmentBundleZip,
} from 'store/assessments/types';
import {
  createAction,
  createAsyncAction,
  Action as ReduxAction,
} from 'typesafe-actions';
import { FieldInstance, FieldResult, UUID } from 'types/types';
import { BarCodeScannerSearch } from 'store/app/types';
import { PlanPinSearch } from 'store/plan/actions';
import { PinNew, PinNewWithName } from 'store/plan/types';
import { AssessmentDelta } from 'store/changes/types';
import { AssessmentPhpResponse } from 'types/Endpoints/AssessmentsPhp';
import { FileTypeGroupsEnum } from 'service/file/types';

export const CREATE_ASSESSMENT = createAction(
  '@ASSESSMENTS/CREATE_ASSESSMENT',
)<{
  assetId: string;
  templateId: string;
}>();

export const CREATE_ASSESSMENT_STEP1 = createAction(
  '@ASSESSMENTS/CREATE_ASSESSMENT_STEP1',
)<{
  assetId: string;
  complianceSubtypeId: string;
}>();

export const DELETE_ASSESSMENT = createAction(
  '@ASSESMENTS/DELETE_ASSESSMENT',
)();

export const UPDATE_ASSESSMENT = createAction(
  '@ASSESSMENTS/UPDATE_ASSESSMENT',
)();

export const LOAD_ASSESSMENT = createAsyncAction(
  '@ASSESSMENTS/LOAD_ASSESSMENT_REQUEST',
  '@ASSESSMENTS/LOAD_ASSESSMENT_RESPONSE',
  '@ASSESSMENTS/LOAD_ASSESSMENT_FAILURE',
)<LoadAssessment, NonNullable<AssessmentState['assessment']>, undefined>();

export const doAseLoadPdf = createAction('doAseLoadPdf')<LoadAssessmentPDF>();

export const doAseLoadBundleZip =
  createAction('doAseLoadBundleZip')<LoadAssessmentBundleZip>();

export const doAseLoadElementPdf = createAction(
  'doAseLoadElementPdf',
)<LoadAssessmentElementPDF>();

export const CLOSE_ASSESSMENT = createAction('@ASSESSMENTS/CLOSE_ASSESSMENT')();

export const UNLOAD_ASSESSMENT = createAction(
  '@ASSESSMENTS/UNLOAD_ASSESSMENT',
)();

export const SUBMIT_ASSESSMENT = createAction(
  '@ASSESSMENTS/SUBMIT_ASSESSMENT',
)();

export const doAseShowValidationErrors = createAction(
  'doAseShowValidationErrors',
)<boolean>();

export const doAseSetSectionIsCollapsed = createAction(
  'doAseSetSectionIsCollapsed',
)<{
  sectionId: string;
  isCollapsed: boolean;
}>();

export const doAseApprovePrecheck = createAction('@doAseApprovePrecheck')();

export const doAseApprove = createAction('@doAseApprove')<{
  actionTimescaleUpdateReviewed: boolean;
  actionTimescaleDate: string;
  note: string;
}>();
export const doAseUnapprove = createAction('@doAseUnapprove')<{
  note: string;
}>();
export const doAseUnsubmit = createAction('@doAseUnsubmit')<{
  notify: boolean;
  note: string;
}>();

export const doAseSaveAssetDetails = createAction('doAseSaveAssetDetails')<{
  set: Partial<AssessmentResults['asset']>;
}>();

export const doAseSaveSummary = createAction('@ASSESSMENTS/doAseSaveSummary')<{
  data: Details;
}>();

export const doAseUpdateSection = createAction('doAseUpdateSection')<{
  id: string;
  data: SectionResult;
}>();

export const doAseUpdateInstanceCheckResult = createAction(
  'doAseUpdateInstanceCheckResult',
)<AseResultInstanceCheck>();

export const doAseSetQuestionResult = createAction('doAseSetQuestionResult')<{
  questionId: string;
  fieldInstanceId: string | null;
  answerId: string | undefined;
  comment?: string;
  actionsAfter?: ReduxAction[];
}>();

export const doAseUpdateQuestion = createAction('doAseUpdateQuestion')<{
  questionId: string;
  fieldInstanceId: string | null;
  // undefined to not update
  answerId?: string;
  // undefined to not update
  checklistItemResults?: Record<string, ChecklistItemResultEnum | null>;
  comment?: string;
  actionsAfter?: ReduxAction[];
}>();

export const doAseDeleteQuestionResult = createAction(
  'doAseDeleteQuestionResult',
)<{
  questionId: string;
  fieldInstanceId: string | null;
}>();

export const doAseSetChecklistItemResults = createAction(
  'doAseSetChecklistItemResults',
)<{
  questionId: string;
  fieldInstanceId: string | null;
  results: Record<string, ChecklistItemResultEnum | null> | undefined;
  comment?: string;
  actionsAfter?: ReduxAction[];
}>();

export const doAseCreateAction = createAction('doAseCreateAction')<{
  data: NewAction;
}>();

export const doAseUpdateAction = createAction('doAseUpdateAction')<{
  id: string;
  data: Action;
}>();

export const doAseReviewAction = createAction('doAseReviewAction')<{
  id: string;
  parent: InstanceParentParams;
  reviewStatusId: string;
}>();

export const doAseDeleteAction = createAction('doAseDeleteAction')<{
  id: string;
}>();

export const doAseCreateControl = createAction('doAseCreateControl')<{
  data: NewControl;
}>();

export const doAseUpdateControl = createAction('doAseUpdateControl')<{
  id: string;
  data: Control;
}>();

export const doAseReviewControl = createAction('doAseReviewControl')<{
  id: string;
  parent: InstanceParentParams;
  reviewStatusId: string;
}>();

export const doAseDeleteControl = createAction('doAseDeleteControl')<{
  id: string;
}>();

export const COPY_ASSESSMENT = createAction('@ASSESSMENTS/COPY_ASSESSMENT')<{
  assetIds: string[];
}>();

export const doAseMakeSuggestion = createAction(
  'doAseMakeSuggestion',
)<FindingSuggestion>();

export const doAseFieldInstanceSearchBarcodeForGroup = createAction(
  '@ASSESSMENTS.doAseFieldInstanceSearchBarcodeForGroup',
)<
  {
    groupId: string;
    parent: InstanceParentParams;
  } & BarCodeScannerSearch
>();

export const doAseFieldInstanceSearchBarcode = createAction(
  '@ASSESSMENTS.doAseFieldInstanceSearchBarcode',
)<{ parent: InstanceParentParams } & BarCodeScannerSearch>();

export const doAseFieldInstanceSearchPin = createAction(
  '@ASSESSMENTS.doAseFieldInstanceSearchPin',
)<{ groupId: string; parent: InstanceParentParams } & PlanPinSearch>();

export const doAseDownloadElementPdfsZip = createAction(
  'doAseDownloadElementPdfsZip',
)<{ groupId: string; parent: InstanceParentParams }>();

export const doAseFieldInstanceAddManyExisting = createAction(
  '@doAseFieldInstanceAddManyExisting',
)<{
  instanceIds: string[];
  parent: InstanceParentParams;
}>();

export const doAseFieldInstanceAddExistingModal = createAction(
  '@doAseFieldInstanceAddExistingModal',
)<{
  instanceId: string;
  parent: InstanceParentParams;
}>();

export const doAseOpenFieldInstance = createAction(
  '@ASSESSMENTS.doAseOpenFieldInstance',
)<{
  instanceId: string;
  parent: InstanceParentParams;
  scrollTo: boolean;
}>();

export const doAseFieldInstanceModifyChildInstances = createAction(
  'doAseFieldInstanceModifyChildInstances',
)<{
  parent: NonNullable<InstanceParentParams>;
  addInstanceIds: UUID[];
  removeInstanceIds: UUID[];
}>();

export const doAseFieldInstanceSave = createAction(
  '@ASSESSMENTS.doAseFieldInstanceSave',
)<{
  isAdding: boolean;
  instance: FieldInstance;
  parent: InstanceParentParams;
  newLiteInstances: Record<string, FieldInstance>;
  isLite: boolean;
}>();

export const doAseFieldResultsSave = createAction(
  '@ASSESSMENTS.doAseFieldResultsSave',
)<{
  results: Record<string, FieldResult>;
}>();

export const doAseFieldInstanceDelete = createAction(
  '@ASSESSMENTS.doAseFieldInstanceDelete',
)<{
  instanceId: string;
}>();

export const doAseLightFieldInstanceDelete = createAction(
  '@doAseLightFieldInstanceDelete',
)<{
  instanceId: string;
  parent: NonNullable<InstanceParentParams>;
}>();

export const doAseLightFieldInstanceUndelete = createAction(
  '@doAseLightFieldInstanceUndelete',
)<{
  instanceId: string;
  parent: NonNullable<InstanceParentParams>;
}>();

export const doAseSetChildGroups = createAction('@doAseSetChildGroups')<{
  instanceIds: string[];
  parent: NonNullable<InstanceParentParams>;
}>();

export const doAseReorderChildGroups = createAction(
  '@doAseReorderChildGroups',
)<{ groupId: string; instanceIds: string[]; eventKey: string }>();

export const doAseFieldInstanceDuplicate = createAction(
  'doAseFieldInstanceDuplicate',
)<{
  instanceId: UUID;
  parent: InstanceParentParams;
}>();

export const doAseFieldInstanceUnlink = createAction(
  'doAseFieldInstanceUnlink',
)<{
  instanceId: UUID;
  parent: NonNullable<InstanceParentParams>;
}>();

export const doAseFieldInstanceRelink = createAction(
  'doAseFieldInstanceRelink',
)<{
  instanceId: UUID;
  parent: NonNullable<InstanceParentParams>;
}>();

export const doAseFieldInstanceAddAction = createAction(
  '@ASSESSMENTS.doAseFieldInstanceAddAction',
)<{
  instanceId: string;
  parent: InstanceParentParams;
}>();

export const doAseFieldInstanceAddControl = createAction(
  '@ASSESSMENTS.doAseFieldInstanceAddControl',
)<{
  instanceId: string;
  parent: InstanceParentParams;
}>();

export const doAseOpenQuestion = createAction(
  '@ASSESSMENTS.doAseOpenQuestion',
)<{
  id: string;
  fieldInstanceId: string | null;
  parent: InstanceParentParams;
}>();

export const doAseOpenInstanceCheck = createAction(
  '@ASSESSMENTS.doAseOpenInstanceCheck',
)<{
  id: string;
  instanceId: string;
}>();

export const doAseOpenSection = createAction('@ASSESSMENTS.doAseOpenSection')<{
  id: string;
}>();

export const doAseOpenAction = createAction('@ASSESSMENTS.doAseOpenAction')<{
  id: string;
  parent: InstanceParentParams;
  edit?: boolean;
}>();

export const doAseOpenControl = createAction('@ASSESSMENTS.doAseOpenControl')<{
  id: string;
  parent: InstanceParentParams;
  edit?: boolean;
}>();

export const doAseOpenFindingLocationModal = createAction(
  'doAseOpenFindingLocationModal',
)<{
  eventKey: string;
  value: string;
}>();

export const doAseMergeResults =
  createAction('@doAseMerge2')<AssessmentDelta>();

export const doAseMergeTemplateSuggested = createAction(
  'doAseMergeTemplateSuggested',
)<{
  actionSuggestions?: Assessment['template']['actionSuggestions'];
  controlSuggestions?: Assessment['template']['controlSuggestions'];
}>();

export const doAseLoadPlans = createAction('@doAseLoadPlans')<
  {
    findNoun?: string;
    actionAfterFind?: {
      type: string;
      payload: PlanPinSearch & Record<string, unknown>;
    };
    selectionKey?: string;
    pin?: PinNew;
    pins: PinNewWithName[];
  } & (
    | { planId?: undefined; pinId?: undefined }
    | { planId: string; pinId?: undefined }
    | { planId?: undefined; pinId: string }
  )
>();

export const doAseLoadDataFromNewApi = createAction(
  '@doAseLoadDataFromNewApi',
)<{ response: AssessmentPhpResponse }>();

export const doAseSetGroupFilter = createAction('@doAseSetGroupFilter')<{
  groupId: string;
  data: Assessment['filterGroups'][string];
}>();

export const doAseOpenAssetFiles = createAction('doAseOpenAssetFiles')<{
  limitSelectionToFileGroups?: FileTypeGroupsEnum[];
  eventKey: string;
  multiSelect?: boolean;
  promptMessage?: string;
}>();

export const doAseAddTempRepeatables = createAction('@doAseAddTempRepeatables')<
  Assessment['tempData']['repeatables']
>();

export const doAseSetFlagResult = createAction('@doAseSetFlagResult')<{
  flagId: string;
  comment: string;
  doDelete: boolean;
}>();

export const doAseSaveSignatory = createAction('doAseSaveSignatory')<{
  signatoryId: string;
  name: string;
  signature: string;
  unavailable: boolean;
  timestamp: number;
}>();

export const doAseDeleteSignatory = createAction('doAseDeleteSignatory')<{
  signatoryId: string;
}>();
